import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";

export type AuthProfile = {
  name: string;
  apiUrl: string;
  // Auth-Modus dieses Profils:
  //   'token' (Default; auch fuer alt-persistierte Profile OHNE mode-Feld) =
  //           opaquer Bearer-Token im localStorage.
  //   'sso'   = Cookie-Session (HttpOnly); KEIN Token im Browser, der
  //           api-client sendet stattdessen credentials:'include'.
  mode?: "token" | "sso";
  // Nur im Token-Modus gesetzt.
  token?: string;
};

type WhoamiSnapshot = {
  tokenName: string;
  scopes: string[];
  contractManufacturerId: string | null;
  tokenPrefix: string;
};

type AuthState = {
  profiles: Record<string, AuthProfile>;
  activeProfile: string | null;
  whoami: WhoamiSnapshot | null;
  setProfile: (profile: AuthProfile) => void;
  removeProfile: (name: string) => void;
  setActiveProfile: (name: string | null) => void;
  setWhoami: (whoami: WhoamiSnapshot | null) => void;
  logoutCurrent: () => void;
  logoutAll: () => void;
};

// Subdomain-Praefixe, die wir als "UI-Surface" interpretieren und durch `api.`
// ersetzen. Begruendung pro Eintrag:
//   ui.        — kanonische Wahl aus .env.example (BG_HWIDALLOC_UI_HOSTNAME)
//   app.       — legacy / Marketing-Subdomain (z.B. app.example.com)
//   dashboard. — alternativer Operator-Frontend-Praefix
// Wenn der UI-Host KEINEN dieser Praefixe hat (z.B. hardware-id.app.0047-20…
// — `app.` steht hier in der Mitte, nicht am Anfang), stellen wir `api.` vor
// den vollstaendigen Host. Dieselbe Konvention erwartet auch der i18n-
// Placeholder in auth.json (`https://api.hardware-id.app.bauer-group.com`).
const UI_SUBDOMAIN_PREFIX = /^(ui|app|dashboard)\./;

/**
 * Leitet eine sinnvolle Default-API-URL aus dem aktuellen UI-Host ab.
 *
 * Reine Funktion — nimmt `protocol`/`hostname` als Argumente, damit Tests die
 * verschiedenen Topologien ohne jsdom-Tricks abdecken koennen. Aufrufer ist
 * fuer die SSR-Guard (`typeof window`) verantwortlich.
 */
export function deriveDefaultApiUrl(protocol: string, hostname: string): string {
  if (hostname === "localhost" || hostname === "127.0.0.1") {
    return "http://localhost:8080";
  }
  if (UI_SUBDOMAIN_PREFIX.test(hostname)) {
    return `${protocol}//${hostname.replace(UI_SUBDOMAIN_PREFIX, "api.")}`;
  }
  return `${protocol}//api.${hostname}`;
}

const DEFAULT_API_URL =
  typeof window === "undefined"
    ? "http://localhost:8080"
    : deriveDefaultApiUrl(window.location.protocol, window.location.hostname);

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      profiles: {},
      activeProfile: null,
      whoami: null,
      setProfile: (profile) =>
        set((state) => ({
          profiles: { ...state.profiles, [profile.name]: profile },
          activeProfile: state.activeProfile ?? profile.name,
        })),
      removeProfile: (name) =>
        set((state) => {
          const next = { ...state.profiles };
          delete next[name];
          const nextActive =
            state.activeProfile === name ? null : state.activeProfile;
          return { profiles: next, activeProfile: nextActive };
        }),
      setActiveProfile: (name) => set({ activeProfile: name, whoami: null }),
      setWhoami: (whoami) => set({ whoami }),
      logoutCurrent: () => {
        const { activeProfile, profiles } = get();
        if (!activeProfile) return;
        const next = { ...profiles };
        delete next[activeProfile];
        set({ profiles: next, activeProfile: null, whoami: null });
      },
      logoutAll: () => set({ profiles: {}, activeProfile: null, whoami: null }),
    }),
    {
      name: "hwidalloc.auth",
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        profiles: state.profiles,
        activeProfile: state.activeProfile,
        // whoami wird persistiert, damit Sidebar-/Button-Scope-Gates ohne
        // Flackern beim Reload korrekt rendern. Aktualisierung passiert
        // beim naechsten authed-Layout-Mount via TanStack-Query.
        whoami: state.whoami,
      }),
    },
  ),
);

export function getActiveProfile(): AuthProfile | null {
  const { activeProfile, profiles } = useAuthStore.getState();
  return activeProfile ? (profiles[activeProfile] ?? null) : null;
}

export function getDefaultApiUrl(): string {
  return DEFAULT_API_URL;
}

// sessionStorage-Key: die vor dem SSO-Redirect gewaehlte API-URL. Uebersteht
// den Cross-Origin-Round-Trip (SPA -> API -> IdP -> API -> SPA-Callback), damit
// der Callback /me gegen genau diese API probt (statt nur den Default-Host).
export const SSO_RETURN_APIURL_KEY = "hwidalloc.sso.apiUrl";

// Stable empty-array reference. `useAuthStore((s) => s.whoami?.scopes ?? [])`
// would return a brand-new array on every store-rerender when whoami is null,
// which trips Zustand's useSyncExternalStore tear-detection check and pushes
// React into an infinite render loop during unmount/cleanup. Reusing this
// constant keeps the reference stable.
const EMPTY_SCOPES: readonly string[] = Object.freeze([]);

/** Snapshot des Scope-Sets aus dem aktuell geladenen whoami. */
export function useScopes(): readonly string[] {
  return useAuthStore((s) => s.whoami?.scopes ?? EMPTY_SCOPES);
}
